import { FormEvent, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw, Image as ImageIcon, X } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import MediaPicker from "@/components/admin/MediaPicker";

interface TestimonialRow {
  id: string;
  client_name: string;
  client_title: string | null;
  testimonial: string;
  rating: number;
  image_url: string | null;
  is_featured: boolean;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

type PubFilter = "all" | "published" | "draft";

const emptyDraft = (): Partial<TestimonialRow> => ({
  client_name: "",
  client_title: "",
  testimonial: "",
  rating: 5,
  image_url: "",
  is_featured: false,
  is_published: false,
  sort_order: 0,
});

const AdminTestimonials = () => {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<TestimonialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pubFilter, setPubFilter] = useState<PubFilter>("all");
  const [editing, setEditing] = useState<Partial<TestimonialRow> | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    document.title = "Testimonials | Hadassah Homes Admin";
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("testimonials")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (err) setError(err.message);
    else setRows((data ?? []) as TestimonialRow[]);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (pubFilter === "published" && !r.is_published) return false;
      if (pubFilter === "draft" && r.is_published) return false;
      return true;
    });
  }, [rows, pubFilter]);

  const togglePublish = async (row: TestimonialRow) => {
    const { data, error: err } = await supabase
      .from("testimonials")
      .update({ is_published: !row.is_published, updated_by: user?.id ?? null } as never)
      .eq("id", row.id)
      .select()
      .single();
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === row.id ? (data as TestimonialRow) : r)));
    toast.success(row.is_published ? "Unpublished" : "Published");
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const name = (editing.client_name ?? "").trim();
    const text = (editing.testimonial ?? "").trim();
    if (!name) return toast.error("Client name is required");
    if (text.length < 10) return toast.error("Testimonial is too short");
    const rating = Number(editing.rating ?? 5);
    if (rating < 1 || rating > 5) return toast.error("Rating must be 1–5");

    setSaving(true);
    const payload: Record<string, unknown> = {
      client_name: name,
      client_title: editing.client_title?.trim() || null,
      testimonial: text,
      rating,
      image_url: editing.image_url?.trim() || null,
      is_featured: !!editing.is_featured,
      is_published: !!editing.is_published,
      sort_order: Number(editing.sort_order ?? 0) || 0,
      updated_by: user?.id ?? null,
    };

    if (editing.id) {
      const { data, error: err } = await supabase
        .from("testimonials")
        .update(payload as never)
        .eq("id", editing.id)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => prev.map((r) => (r.id === editing.id ? (data as TestimonialRow) : r)));
      toast.success("Saved");
    } else {
      const { data, error: err } = await supabase
        .from("testimonials")
        .insert({ ...payload, created_by: user?.id ?? null } as never)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => [data as TestimonialRow, ...prev]);
      toast.success("Created");
    }
    setEditing(null);
  };

  const updateField = <K extends keyof TestimonialRow>(k: K, v: TestimonialRow[K]) =>
    setEditing((prev) => (prev ? { ...prev, [k]: v } : prev));

  return (
    <AdminLayout
      title="Testimonials"
      description="Manage client testimonials shown on the public website. Only published testimonials appear publicly."
      publicHref="/"
      publicLabel="View on homepage"
    >
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <select
            value={pubFilter}
            onChange={(e) => setPubFilter(e.target.value as PubFilter)}
            className="h-10 rounded-none border border-input bg-background px-3 text-sm"
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void load()} disabled={loading} className="rounded-none">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="gold" onClick={() => setEditing(emptyDraft())} className="rounded-none">
              <Plus className="mr-2 h-4 w-4" />
              New testimonial
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No testimonials yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3 hidden md:table-cell">Excerpt</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{r.client_name}</p>
                        {r.client_title ? (
                          <p className="text-[11px] text-muted-foreground">{r.client_title}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                        <p className="line-clamp-2 max-w-xl">{r.testimonial}</p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{r.sort_order}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                            r.is_published
                              ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                              : "bg-muted text-muted-foreground border-border"
                          }`}
                        >
                          {r.is_published ? "Published" : "Draft"}
                        </span>
                        {r.is_featured ? (
                          <span className="ml-1 inline-flex items-center border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
                            Featured
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => togglePublish(r)} className="rounded-none">
                            {r.is_published ? "Unpublish" : "Publish"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditing(r)} className="rounded-none">
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} testimonials
        </p>
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {editing ? (
            <form onSubmit={handleSave}>
              <SheetHeader>
                <SheetTitle className="font-headline text-2xl">
                  {editing.id ? "Edit testimonial" : "New testimonial"}
                </SheetTitle>
                <SheetDescription>
                  Published testimonials appear on the public site.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <Field label="Client name *">
                  <Input
                    required
                    value={editing.client_name ?? ""}
                    onChange={(e) => updateField("client_name", e.target.value as never)}
                    className="rounded-none"
                  />
                </Field>

                <Field label="Client title (optional)">
                  <Input
                    value={editing.client_title ?? ""}
                    onChange={(e) => updateField("client_title", e.target.value as never)}
                    placeholder="Homeowner, Boston"
                    className="rounded-none"
                  />
                </Field>

                <Field label="Testimonial *">
                  <Textarea
                    required
                    rows={6}
                    value={editing.testimonial ?? ""}
                    onChange={(e) => updateField("testimonial", e.target.value as never)}
                    className="rounded-none"
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Rating (1–5)">
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      value={editing.rating ?? 5}
                      onChange={(e) => updateField("rating", (Number(e.target.value) || 5) as never)}
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Sort order">
                    <Input
                      type="number"
                      value={editing.sort_order ?? 0}
                      onChange={(e) => updateField("sort_order", (Number(e.target.value) || 0) as never)}
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <Field label="Client image (optional)">
                  <div className="space-y-2">
                    {editing.image_url ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={editing.image_url}
                          alt=""
                          className="h-16 w-16 border border-border object-cover"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => updateField("image_url", "" as never)}
                          className="rounded-none"
                        >
                          <X className="mr-1 h-3 w-3" /> Remove
                        </Button>
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setPickerOpen(true)}
                        className="rounded-none"
                      >
                        <ImageIcon className="mr-2 h-4 w-4" />
                        Choose from Media Library
                      </Button>
                    </div>
                    <Input
                      value={editing.image_url ?? ""}
                      onChange={(e) => updateField("image_url", e.target.value as never)}
                      placeholder="Or paste a direct image URL"
                      className="rounded-none"
                    />
                  </div>
                </Field>

                <div className="space-y-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editing.is_published}
                      onChange={(e) => updateField("is_published", e.target.checked as never)}
                    />
                    Published
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editing.is_featured}
                      onChange={(e) => updateField("is_featured", e.target.checked as never)}
                    />
                    Featured
                  </label>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" variant="gold" disabled={saving} className="rounded-none">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          ) : null}
        </SheetContent>
      </Sheet>

      <MediaPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(asset) => {
          updateField("image_url", asset.public_url as never);
        }}
      />
    </AdminLayout>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </label>
    {children}
  </div>
);

export default AdminTestimonials;
